'use client';

import type { PropsWithChildren } from 'react';
import React, { createContext, useCallback, useContext, useState } from 'react';
import { UnsavedChangesModal } from '@/app/components/UnsavedChangesModal';

interface UnsavedChangesContextType {
  isDirty: boolean;
  markAsDirty: () => void;
  markAsClean: () => void;
  checkUnsavedChanges: (pendingActionCallback: () => void) => boolean;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextType | undefined>(undefined);

export const useUnsavedChanges = () => {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error('useUnsavedChanges must be used within an UnsavedChangesProvider');
  }
  return { ...context };
};

export const UnsavedChangesProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [isDirty, setIsDirty] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const markAsDirty = useCallback(() => {
    setIsDirty(true);
  }, []);

  const markAsClean = useCallback(() => {
    setIsDirty(false);
  }, []);

  const checkUnsavedChanges = useCallback(
    (actionOnDiscard: () => void): boolean => {
      if (isDirty) {
        setPendingAction(() => actionOnDiscard);
        setIsModalOpen(true);
        return false;
      } else {
        actionOnDiscard();
        return true;
      }
    },
    [isDirty]
  );

  const handleDiscardAndProceed = () => {
    if (pendingAction) {
      pendingAction();
    }
    setIsModalOpen(false);
    setPendingAction(null);
    setIsDirty(false);
  };

  const handleCancelNavigation = () => {
    setIsModalOpen(false);
    setPendingAction(null);
  };

  return (
    <UnsavedChangesContext.Provider value={{ isDirty, markAsDirty, markAsClean, checkUnsavedChanges }}>
      {children}
      <UnsavedChangesModal isOpen={isModalOpen} onClose={handleCancelNavigation} onSave={handleDiscardAndProceed} />
    </UnsavedChangesContext.Provider>
  );
};